/**
 * Responsive typography scale tokens sourced from the Figma
 * "Responsive / Typography" variable collection.
 *
 * Three device modes: Common (default), Mobile, Desktop.
 * Common and Desktop have identical values. Mobile scales down.
 *
 * These are the raw building blocks. Semantic tokens in typography.ts
 * alias into this scale (e.g., B2B heading/h1/font-size → font-size/650).
 *
 * On web, these are injected as CSS custom properties (--xui-font-size-*, --xui-lh-*)
 * with @media overrides for mobile. Components consume a single CSS variable
 * and scale automatically by viewport.
 *
 * @see {@link ../styles/typography-css.ts} for CSS generation
 * @see {@link ./breakpoints.ts} for breakpoint definitions
 */

export interface ResponsiveValue {
  common: number;
  mobile: number;
}

export type ScaleStep =
  | "75"
  | "100"
  | "125"
  | "150"
  | "175"
  | "200"
  | "250"
  | "300"
  | "350"
  | "450"
  | "550"
  | "650"
  | "750";

export const SCALE_STEPS: ScaleStep[] = [
  "75",
  "100",
  "125",
  "150",
  "175",
  "200",
  "250",
  "300",
  "350",
  "450",
  "550",
  "650",
  "750",
];

/**
 * The display/font-size axis extends the base scale with three larger steps
 * (800/850/900) used by Corpsite display typography. Only `fontSize` and
 * `lineHeightDisplay` define these — `lineHeightCompact`/`lineHeightText` stop
 * at 750, mirroring the Figma "Responsive / Typography" collection.
 */
export type DisplayScaleStep = ScaleStep | "800" | "850" | "900";

export const DISPLAY_SCALE_STEPS: DisplayScaleStep[] = [
  ...SCALE_STEPS,
  "800",
  "850",
  "900",
];

/** Font-size scale: 16 steps from 10px to 96px */
export const fontSize: Record<DisplayScaleStep, ResponsiveValue> = {
  "75": { common: 10, mobile: 10 },
  "100": { common: 12, mobile: 11 },
  "125": { common: 14, mobile: 12 },
  "150": { common: 16, mobile: 14 },
  "175": { common: 18, mobile: 16 },
  "200": { common: 20, mobile: 18 },
  "250": { common: 24, mobile: 20 },
  "300": { common: 28, mobile: 24 },
  "350": { common: 32, mobile: 28 },
  "450": { common: 40, mobile: 32 },
  "550": { common: 48, mobile: 40 },
  "650": { common: 56, mobile: 48 },
  "750": { common: 64, mobile: 56 },
  "800": { common: 72, mobile: 64 },
  "850": { common: 80, mobile: 72 },
  "900": { common: 96, mobile: 80 },
};

/** Line-height/display: tight line-heights for headings (roughly 1:1 with font-size) */
export const lineHeightDisplay: Record<DisplayScaleStep, ResponsiveValue> = {
  "75": { common: 10, mobile: 10 },
  "100": { common: 12, mobile: 11 },
  "125": { common: 14, mobile: 12 },
  "150": { common: 16, mobile: 14 },
  "175": { common: 18, mobile: 16 },
  "200": { common: 20, mobile: 18 },
  "250": { common: 24, mobile: 20 },
  "300": { common: 28, mobile: 24 },
  "350": { common: 32, mobile: 28 },
  "450": { common: 40, mobile: 32 },
  "550": { common: 48, mobile: 40 },
  "650": { common: 56, mobile: 48 },
  "750": { common: 64, mobile: 56 },
  "800": { common: 72, mobile: 64 },
  "850": { common: 80, mobile: 72 },
  "900": { common: 96, mobile: 80 },
};

/** Line-height/compact: moderate line-heights for UI body text */
export const lineHeightCompact: Record<ScaleStep, ResponsiveValue> = {
  "75": { common: 14, mobile: 13 },
  "100": { common: 16, mobile: 14 },
  "125": { common: 18, mobile: 16 },
  "150": { common: 20, mobile: 18 },
  "175": { common: 24, mobile: 20 },
  "200": { common: 26, mobile: 24 },
  "250": { common: 30, mobile: 26 },
  "300": { common: 36, mobile: 30 },
  "350": { common: 42, mobile: 36 },
  "450": { common: 52, mobile: 42 },
  "550": { common: 62, mobile: 52 },
  "650": { common: 72, mobile: 62 },
  "750": { common: 82, mobile: 72 },
};

/** Line-height/text: generous line-heights for paragraphs and reading text */
export const lineHeightText: Record<ScaleStep, ResponsiveValue> = {
  "75": { common: 14, mobile: 13 },
  "100": { common: 18, mobile: 14 },
  "125": { common: 20, mobile: 16 },
  "150": { common: 22, mobile: 18 },
  "175": { common: 26, mobile: 20 },
  "200": { common: 28, mobile: 24 },
  "250": { common: 34, mobile: 26 },
  "300": { common: 40, mobile: 30 },
  "350": { common: 44, mobile: 36 },
  "450": { common: 56, mobile: 42 },
  "550": { common: 66, mobile: 52 },
  "650": { common: 78, mobile: 62 },
  "750": { common: 88, mobile: 72 },
};

export const responsiveTypographyScale = {
  fontSize,
  lineHeightDisplay,
  lineHeightCompact,
  lineHeightText,
} as const;
